import {
  HumanitarianStatus,
  HumanitarianStatusType,
  ViewerQuery,
} from '@sandtech-hub-monorepo/ehub-client';
import { useState } from 'react';
import { DiversityAndHumanitarianModal } from './DiversityAndHumanitarianModal';
import { PersonalInformationModal } from './PersonalInformationModal';

export const hasCompletedDataCollection = (viewer?: ViewerQuery['viewer']) => {
  return true;
};

/**
 * Check if user has completed all personal information fields
 */
function hasCompletedPersonalInfo(viewer?: ViewerQuery['viewer']): boolean {
  if (!viewer) return false;

  return !!(
    viewer.highestLevelOfStudy &&
    viewer.fieldOfStudy &&
    viewer.isEmployed != null &&
    (viewer.isEmployed ? viewer.employmentType : true) &&
    viewer.openToNewPositions != null &&
    viewer.topCareerGoal &&
    viewer.previouslyEnrolledInALX != null &&
    viewer.socialLinks?.linkedIn &&
    viewer.skills &&
    viewer.skills.length >= 3
  );
}

/**
 * Check if user has completed all diversity and humanitarian status fields
 */
function hasCompletedDiversityInfo(viewer?: ViewerQuery['viewer']): boolean {
  if (!viewer) return false;

  const hasBasicDiversityInfo = !!(
    viewer.guardianship &&
    viewer.locationDescription &&
    viewer.livingCircumstances &&
    viewer.householdMonthlyIncomeUSD != null &&
    viewer.disability &&
    viewer.humanitarianStatus
  );

  if (!hasBasicDiversityInfo) return false;

  // If humanitarian status is Yes, then humanitarianStatusType is required
  if (viewer.humanitarianStatus === HumanitarianStatus.Yes) {
    if (!viewer.humanitarianStatusType) return false;

    // If type is Refugee, refugee card is required
    if (
      viewer.humanitarianStatusType === HumanitarianStatusType.Refugee &&
      !viewer.refugeeCard
    ) {
      return false;
    }
  }

  return true;
}

interface Props {
  viewer?: ViewerQuery['viewer'];
  onComplete: () => void;
  completionLabel: string;
}

type ModalStep = 'personalInfo' | 'diversity';

export const DataCollectionModal = ({
  viewer,
  onComplete,
  completionLabel,
}: Props) => {
  // Check if user has completed information sections
  const personalInfoCompleted = hasCompletedPersonalInfo(viewer);
  const diversityInfoCompleted = hasCompletedDiversityInfo(viewer);

  const [modalStep, setModalStep] = useState<ModalStep>(
    personalInfoCompleted ? 'diversity' : 'personalInfo'
  );

  const handlePersonalInfoComplete = () => {
    // After personal info, check if diversity is needed
    if (!diversityInfoCompleted) {
      setModalStep('diversity');
    } else {
      // Diversity already completed, enroll directly
      onComplete();
    }
  };

  return (
    <>
      {modalStep === 'personalInfo' && (
        <PersonalInformationModal
          onComplete={handlePersonalInfoComplete}
          defaultValues={{
            highestLevelOfStudy: viewer?.highestLevelOfStudy,
            fieldOfStudy: viewer?.fieldOfStudy,
            isEmployed: viewer?.isEmployed,
            employmentType: viewer?.employmentType,
            openToNewPositions: viewer?.openToNewPositions,
            topCareerGoal: viewer?.topCareerGoal,
            previouslyEnrolledInALX: viewer?.previouslyEnrolledInALX,
            linkedIn: viewer?.socialLinks?.linkedIn,
            skills: viewer?.skills,
          }}
          allInfoCompleted={diversityInfoCompleted}
        />
      )}
      {modalStep === 'diversity' && (
        <DiversityAndHumanitarianModal
          onComplete={onComplete}
          defaultValues={{
            guardianship: viewer?.guardianship,
            locationDescription: viewer?.locationDescription,
            livingCircumstances: viewer?.livingCircumstances,
            householdMonthlyIncomeUSD:
              viewer?.householdMonthlyIncomeUSD?.toString(),
            disability: viewer?.disability,
            humanitarianStatus: viewer?.humanitarianStatus,
            humanitarianStatusType: viewer?.humanitarianStatusType,
            refugeeCard: viewer?.refugeeCard,
          }}
          completionLabel={completionLabel}
        />
      )}
    </>
  );
};
